import isEqual from 'lodash/isEqual'

import get from 'lodash/get'

import { canLoadMoreitins } from '../../../util/canLoadMoreitins'

import { airSearch } from '../../../services/airSearch'
import { buildFlySearchDisplayFiltersObj, scrollTopWhenRequired } from '../../../util/listingsHelpers'

import { wrapPromiseForStreaming } from '../../../util/wrapPromiseForStreaming'
import { setInitialFilterInfo } from '../../index'
import { handleError } from '../helpers/handleError/handleError.js'
import { getLoadingScreenMessage } from './helpers'

import updateListingsWithCheapestBadge from '@mfs/client/listings-client/actions/listings/fetchData/updateListingsWithCheapestBadge'
import { setCheapestPrice } from '@mfs/client/listings-client/reducers/cheapestPrice/cheapestPrice'
import {
  setEmployeeSessionKey,
  setStandardSessionKey,
} from '@mfs/client/listings-client/reducers/filters/employeeDiscount/employeeDiscount'
import {
  updateFilterInfoSortOptions,
  updateIncludedCarriers,
} from '@mfs/client/listings-client/reducers/filters/filterInfo/filterInfo'
import { setSortOptions } from '@mfs/client/listings-client/reducers/sortOptions/sortOptions'
import { updateFilterDefaultsWithPreservedState } from '@mfs/client/listings-client/util/filter/updateFilterDefaultsWithPreservedState'
import { setCheapestItinerary } from '@mfs/client/utils/setCheapestItinerary/setCheapestItinerary'
import PCLNAnalytics from '@pcln/analytics'
import { setAirSearchPromiseRequest } from '../../../reducers/airSearchPromise'
import {
  setIsFiltersAppliedForGa,
  setOverfilteredBannerText,
  setOverfilteredStatus,
} from '../../../reducers/flags'
import { setFlySearchResponseData } from '../../../reducers/flySearchResponseData'
import {
  updateBrandReference,
  updateCheckIfNotInitialLoadOfListing,
  updateDisplayInfiniteScrollLoadMore,
  updateListingsFetchDataSuccess,
  updateListingsHasErrored,
  updateListingsIsLoading,
  updateListingsLoadingMessage,
  updateRecommendedListings,
} from '../../../reducers/listings'
import { setListingsMetaData } from '../../../reducers/listingsMetaData/listingsMetaData'
import { isAnyFilterApplied } from '@mfs/client/listings-client/services/airSearch/airSearchRequest/helpers/isAnyFilterApplied'

/**
 * Make airSearch call for filtering, sorting, and slice selection
 */
export function listingsFilterCall(_, displayType = '') {
  return (dispatch, getState) => {
    const PCLN_BOOTSTRAP_DATA = getState().pclnBootstrapData

    const flySearchResponseData = structuredClone(getState().flySearchResponseData)

    const isSliceSelection = displayType === 'sliceSelection'
    const sortOptions = getState().sortOptions

    let loadingScreenMessage = getLoadingScreenMessage(isSliceSelection, sortOptions)
    const airSearchRequestModel = getState().airSearchRequestModel

    const { defaults } = getState().filterInfo

    dispatch(updateListingsIsLoading(true))
    dispatch(updateCheckIfNotInitialLoadOfListing(true))
    dispatch(updateListingsLoadingMessage(loadingScreenMessage))
    dispatch(updateListingsHasErrored(false))

    const activeTabId = getState().activeTabId
    const currentSliceId = activeTabId - 1

    buildFlySearchDisplayFiltersObj({
      filter: structuredClone(getState().filterInfo),
      activeTabId,
      isSliceSelection,
      airSearchRequestModel,
      PCLN_BOOTSTRAP_DATA,
    })

    const prevSortOptionSelection = airSearchRequestModel?.slices?.[0]?.sortOptions?.[0]
    const newSortOptionSelection = sortOptions?.[activeTabId]
    const isSortOptionChanged = displayType === 'filter' && prevSortOptionSelection !== newSortOptionSelection
    const isMD = PCLN_BOOTSTRAP_DATA?.isMultiDestination

    const isFiltersApplied = isAnyFilterApplied({ airSearchRequestModel, filterDefaults: defaults })

    /**
     * If RECOMMENDED sort and any filters applied, set to LOWEST_PRICE
     * If LOWEST_PRICE sort and no filters applied, set to RECOMMENDED
     *
     * Leave all other sort options unchanged
     */
    if (!isSortOptionChanged && activeTabId === 1 && !isMD) {
      if (isFiltersApplied && sortOptions?.[activeTabId] === 'RECOMMENDED') {
        const newSortOptions = { ...sortOptions, [activeTabId]: 'LOWEST_PRICE' }
        dispatch(setSortOptions(newSortOptions))
      } else if (
        !isFiltersApplied &&
        prevSortOptionSelection === 'LOWEST_PRICE' &&
        PCLN_BOOTSTRAP_DATA?.isNotReshopAndWLAndPackages
      ) {
        const newSortOptions = { ...sortOptions, [activeTabId]: 'RECOMMENDED' }
        dispatch(setSortOptions(newSortOptions))
      }
    }

    if (!isSliceSelection) {
      dispatch(setIsFiltersAppliedForGa(true))
    }

    if (isSliceSelection) {
      flySearchResponseData.filters[currentSliceId] = {
        sliceRefId: currentSliceId,
        filterDefaults: getState().filterInfo.defaults,
        AIRLINES: getState().airlinesFilter,
        AIRPORTS: getState().airportsFilter,
        ATTRIBUTES: getState().attributesFilter,
        DEFAULT_TAB_ITEMS: getState().filterInfo.defaults.DEFAULT_TAB_ITEMS,
        timeDefaults: getState().filterInfo.defaults.TIME,
        NUM_OF_STOPS: getState().stopsFilter,
        SLICE_ARRAY: getState().filterInfo.defaults.SLICE_ARRAY,
        TIME: getState().timeFilter,
        TIMEBANDS: getState().filterInfo?.values?.[`timebands${currentSliceId}`],
      }
      dispatch(setFlySearchResponseData(flySearchResponseData))
    }
    // Get employee discount state
    const employeeDiscountState = getState().employeeDiscount
    const isEmployeeDiscountEnabled = employeeDiscountState?.isEnabled || false
    const standardSessionKey = employeeDiscountState?.standardSessionKey || ''
    const employeeSessionKey = employeeDiscountState?.employeeSessionKey || ''

    // Log search with employee discount state
    PCLNAnalytics.log({
      category: 'Flight Listings',
      action: 'Search with Employee Discount',
      label: isEmployeeDiscountEnabled ? 'EMPLOYEE_DISCOUNT_ON' : 'EMPLOYEE_DISCOUNT_OFF',
      employeeDiscountEnabled: isEmployeeDiscountEnabled,
      searchType: isSliceSelection ? 'slice_selection' : 'filter',
      hasEmployeeSessionKey: !!employeeSessionKey,
      hasStandardSessionKey: !!standardSessionKey,
      requestId: airSearchRequestModel?.requestId,
    })

    dispatch(
      setAirSearchPromiseRequest(
        wrapPromiseForStreaming(
          airSearch({
            pclnBootstrapData: PCLN_BOOTSTRAP_DATA,
            fromFilters: true,
            fromSlice: isSliceSelection,
            sortOptions: getState().sortOptions,
            airSearchRequestModel,
            filterDefaults: defaults,
            cguid: PCLN_BOOTSTRAP_DATA?.requestLocals?.webstats?.clientGUID,
            filterInfo: getState().filterInfo,
            activeTabId,
            listingsMetaData: getState().listingsMetaData,
            // Employee discount parameters
            employeeDiscountEnabled: isEmployeeDiscountEnabled,
            standardSessionKey,
            employeeSessionKey,
          })
            .then((res) => {
              const { response } = res
              const { listingsMetaData, filterDefaults, listings, recommendedListings } = response

              // Store session key based on employee discount toggle state
              const searchSessionKey = listingsMetaData?.searchSessionKey
              const isEmployeeDiscountEnabled = getState().employeeDiscount?.isEnabled

              if (searchSessionKey) {
                if (isEmployeeDiscountEnabled) {
                  // Store as employee session key (cache it for future use)
                  dispatch(setEmployeeSessionKey(searchSessionKey))
                } else {
                  // Store as standard session key (cache it for future use)
                  dispatch(setStandardSessionKey(searchSessionKey))
                }
              }

              if (isSliceSelection) {
                // Preserve/persist selected stops in UI state
                updateFilterDefaultsWithPreservedState({
                  filterDefaults,
                  previousStopsFilterState: getState().stopsFilter,
                })
              }

              const { upperBound, totalFilteredItineraries } = listingsMetaData

              const loadMore = canLoadMoreitins(upperBound, totalFilteredItineraries)

              const brandReference = get(response, 'brandReference', [])

              if (brandReference && !isEqual(get(getState(), 'brandReference'), brandReference)) {
                dispatch(updateBrandReference(brandReference))
              }

              /**
               * TODO: Refactor this abomination
               */
              const listingsObj = {
                response: listings,
              }

              listingsObj.response = updateListingsWithCheapestBadge({
                response,
              })

              let processedRecommendedListings = recommendedListings

              // isSliceSelection is true for request for next slice
              if (isSliceSelection) {
                listingsObj.response = setCheapestItinerary(listingsObj.response)
                dispatch(
                  setCheapestPrice({
                    sliceId: currentSliceId,
                    cheapestPrice: listingsObj.response.find((i) => i.isCheapest)?.price?.[1]?.amount,
                  })
                )
              } else {
                const cheapestPrice = getState()?.cheapestPrice?.find((i) => i.sliceId === activeTabId - 1)
                if (cheapestPrice) {
                  listingsObj.response = setCheapestItinerary(
                    listingsObj.response,
                    cheapestPrice.cheapestPrice
                  )
                  processedRecommendedListings = setCheapestItinerary(
                    recommendedListings,
                    cheapestPrice.cheapestPrice
                  )
                }
              }

              if (
                !PCLN_BOOTSTRAP_DATA?.isMobile &&
                processedRecommendedListings?.length > 0 &&
                PCLN_BOOTSTRAP_DATA?.isNotReshopAndWLAndPackages
              ) {
                processedRecommendedListings = [
                  ...processedRecommendedListings,
                  { isSponsoredAd: true, adId: 'div-gpt-ad-1774275433713-0' },
                ]
              }

              dispatch(updateListingsIsLoading(false))
              dispatch(updateListingsFetchDataSuccess(listingsObj))
              dispatch(
                updateRecommendedListings({
                  recommendedListings: processedRecommendedListings,
                  activeTabId,
                })
              )

              dispatch(updateDisplayInfiniteScrollLoadMore({ loadMore, activeTabId }))
              dispatch(setOverfilteredBannerText(false))
              dispatch(setOverfilteredStatus(false))
              dispatch(setListingsMetaData(get(response, 'listingsMetaData', null)))

              // If included-airlines in URL, persist across UI changes (slice selection, filter, load more)
              if (PCLN_BOOTSTRAP_DATA?.searchRequest?.params?.['included-airlines']) {
                dispatch(
                  updateIncludedCarriers(PCLN_BOOTSTRAP_DATA.searchRequest.params['included-airlines'])
                )

                if (['sliceSelection', 'employeeDiscountToggle'].includes(displayType)) {
                  const includedCarriers =
                    airSearchRequestModel.filter?.sliceFilters?.[currentSliceId]?.includedCarriers

                  filterDefaults.AIRLINES = filterDefaults.AIRLINES.map((slice) => ({
                    ...slice,
                    checked: includedCarriers?.length > 0,
                    disabled: includedCarriers?.length === 1,
                  }))
                }
              }

              // Set Filter Defaults
              if (displayType === 'sliceSelection' || displayType === 'employeeDiscountToggle') {
                dispatch(setInitialFilterInfo({ payload: filterDefaults, sbsParams: { activeTabId } }))
              }

              if (displayType === 'filter') {
                dispatch(updateFilterInfoSortOptions(filterDefaults.SORT_OPTIONS))
              }

              scrollTopWhenRequired(displayType)
            })
            .catch((error) => {
              window?.newrelic?.noticeError(error)
              handleError({ dispatch, error, requestType: 'FILTER' })
              if (activeTabId !== 1) {
                dispatch(updateListingsFetchDataSuccess({ response: [] }))
                dispatch(updateDisplayInfiniteScrollLoadMore({ loadMore: false, activeTabId }))
              }
            })
        )
      )
    )
  }
}
